/**
 * MiniGraphToggleButton — flips the per-object `miniGraphOpenByObject`
 * flag in `vqeDdlStore`. Lives in the dependency-sidebar slot so the user
 * can opt into the neighbors-only view without leaving the editor surface.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice H3.
 */
import { ListTree, Network } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { miniGraphObjectKey, useVqeDdlStore } from '../../store';

export const MINI_GRAPH_TOGGLE_TESTID = 'mini-graph-toggle';

export interface MiniGraphToggleButtonProps {
  readonly connectionId: string | null | undefined;
  readonly database: string | null | undefined;
  readonly schema: string | null | undefined;
  readonly objectName: string | null | undefined;
  readonly testId?: string;
}

export function MiniGraphToggleButton({
  connectionId,
  database,
  schema,
  objectName,
  testId = MINI_GRAPH_TOGGLE_TESTID,
}: MiniGraphToggleButtonProps) {
  const key = miniGraphObjectKey({ connectionId, database, schema, objectName });
  const open = useVqeDdlStore((s) => s.miniGraphOpenByObject[key] ?? false);
  const setOpen = useVqeDdlStore((s) => s.setMiniGraphOpen);
  const { track } = useAnalytics();

  const onClick = () => {
    const next = !open;
    setOpen(key, next);
    track('feature:vqe_ddl_mini_graph_toggle', { enabled: next });
  };

  const disabled = !objectName;

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-7 w-full gap-1.5 text-[11px]"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      data-mini-graph-open={open ? 'true' : 'false'}
      aria-pressed={open}
    >
      {open ? (
        <ListTree className="size-3.5" />
      ) : (
        <Network className="size-3.5" />
      )}
      {open ? 'Hide mini-graph' : 'Show mini-graph'}
    </Button>
  );
}
